/**
 * VehicleQuickCapture — WP-06 Part 2: the mobile path for getting
 * year / make / model / VIN onto an estimate's vehicle.
 *
 * Web clip needs a desktop browser extension, so on a phone it can never
 * complete.  This drawer is the phone-native equivalent and needs nothing but a
 * keyboard: paste (or type) whatever you have — a customer's text message, a
 * dealer listing, an insurance card, the VIN off the door jamb — and it pulls
 * out year / make / model / VIN.
 *
 * Design rules this component must keep:
 *  - Nothing the user pasted is silently dropped: the parser's `problems`,
 *    `assumptions` and `unparsed` lists are all shown.
 *  - Every parsed value lands in an editable field — the parse is a suggestion,
 *    the user is the authority.
 *  - A valid VIN can be decoded via the existing NHTSA-backed
 *    POST /vehicles/vin-lookup, which needs no AI key and no upload.
 *  - Works at 390px: single column, full-width controls, bottom sheet.
 *
 * data-testids: vqc-drawer, vqc-paste, vqc-year, vqc-make, vqc-model, vqc-vin,
 *   vqc-decode, vqc-save, vqc-problems, vqc-unparsed, vqc-assumptions, vqc-no-vehicle
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  message,
} from 'antd';
import { ScanOutlined } from '@ant-design/icons';
import { vehicleApi } from '../../services/api';
import CameraCaptureField from '../Shared/CameraCaptureField';
import {
  parseVehicleText,
  isValidVin,
  normalizeVinCandidate,
  maxVehicleYear,
  MIN_VEHICLE_YEAR,
  MAX_MAKE_MODEL_LENGTH,
} from '../../utils/vehicleParse';
import { logger } from '../../utils/logger';

const { Text, Paragraph } = Typography;

export interface VehicleQuickCaptureProps {
  open: boolean;
  onClose: () => void;
  /** The vehicle to update. Null/undefined when the record has no vehicle yet. */
  vehicleId: number | null | undefined;
  /** Current values, used as the starting point so a blank field is never saved over a real one. */
  initial?: {
    year?: number | null;
    make?: string | null;
    model?: string | null;
    vin?: string | null;
  };
  /** Called after a successful save so the parent can refetch. */
  onSaved?: () => void;
}

interface FieldValues {
  year: number | null;
  make: string;
  model: string;
  vin: string;
}

const EMPTY_FIELDS: FieldValues = { year: null, make: '', model: '', vin: '' };

const VehicleQuickCapture: React.FC<VehicleQuickCaptureProps> = ({
  open,
  onClose,
  vehicleId,
  initial,
  onSaved,
}) => {
  const [pasted, setPasted] = useState('');
  const [fields, setFields] = useState<FieldValues>(EMPTY_FIELDS);
  const [touched, setTouched] = useState<Record<keyof FieldValues, boolean>>({
    year: false,
    make: false,
    model: false,
    vin: false,
  });
  const [decoding, setDecoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decodeNotes, setDecodeNotes] = useState<string[]>([]);

  const parsed = useMemo(() => (pasted.trim() === '' ? null : parseVehicleText(pasted)), [pasted]);

  // Seed the form with the vehicle's current values each time the drawer opens,
  // so the user edits what is really stored rather than a blank slate (which
  // would make "no change" indistinguishable from "cleared").
  const initialYear = initial?.year ?? null;
  const initialMake = initial?.make ?? '';
  const initialModel = initial?.model ?? '';
  const initialVin = initial?.vin ?? '';
  useEffect(() => {
    if (!open) return;
    setPasted('');
    setDecodeNotes([]);
    setTouched({ year: false, make: false, model: false, vin: false });
    setFields({ year: initialYear, make: initialMake, model: initialModel, vin: initialVin });
  }, [open, initialYear, initialMake, initialModel, initialVin]);

  /**
   * Parse on every change and push results into fields the user has not edited.
   * A field the user typed in is never overwritten by a later parse.
   */
  const handlePasteChange = useCallback(
    (value: string) => {
      setPasted(value);
      if (value.trim() === '') return;
      const result = parseVehicleText(value);
      setFields((prev) => ({
        year: touched.year ? prev.year : result.year ?? prev.year,
        make: touched.make ? prev.make : result.make ?? prev.make,
        model: touched.model ? prev.model : result.model ?? prev.model,
        vin: touched.vin ? prev.vin : result.vin ?? prev.vin,
      }));
    },
    [touched],
  );

  const setField = useCallback(<K extends keyof FieldValues>(key: K, value: FieldValues[K]) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const vinIsValid = fields.vin.trim() !== '' && isValidVin(fields.vin);

  const handleDecode = useCallback(async () => {
    if (!vinIsValid) return;
    setDecoding(true);
    setDecodeNotes([]);
    try {
      const resp = await vehicleApi.vinLookup(normalizeVinCandidate(fields.vin));
      const decoded = resp.data?.decoded;
      if (!decoded) {
        message.error('VIN decode returned nothing — enter the details manually.');
        return;
      }
      // Computed OUTSIDE the state updater: an updater must be pure, and React
      // invokes it twice in development — collecting notes inside it duplicated
      // every message.
      // Computed OUTSIDE the state updater: an updater must be pure, and React
      // invokes it twice in development — collecting notes inside it duplicated
      // every message on the live page.
      const prev = fields;
      const next = { ...prev };
      const notes: string[] = [];
      // Only fill blanks; a difference from an existing value is reported, not
      // silently applied — the user may know better than vPIC.
      if (decoded.year != null) {
        if (prev.year === null) next.year = decoded.year;
        else if (prev.year !== decoded.year) notes.push(`VIN decodes to year ${decoded.year} (you have ${prev.year}).`);
      }
      if (decoded.make) {
        if (prev.make.trim() === '') next.make = decoded.make;
        else if (prev.make.trim().toLowerCase() !== decoded.make.toLowerCase()) notes.push(`VIN decodes to make "${decoded.make}" (you have "${prev.make}").`);
      }
      if (decoded.model) {
        if (prev.model.trim() === '') next.model = decoded.model;
        else if (prev.model.trim().toLowerCase() !== decoded.model.toLowerCase()) notes.push(`VIN decodes to model "${decoded.model}" (you have "${prev.model}").`);
      }
      if (decoded.year == null && !decoded.make && !decoded.model) {
        notes.push('The VIN decoded, but returned no year / make / model.');
      }
      setFields(next);
      setDecodeNotes(notes);
    } catch (err: any) {
      const detail = err?.response?.data?.error ?? err?.message ?? 'unknown error';
      logger.error('[VehicleQuickCapture] VIN decode failed', detail);
      message.error(`VIN decode failed: ${detail}`);
    } finally {
      setDecoding(false);
    }
  }, [fields, vinIsValid]);

  const buildPatch = useCallback((): Record<string, unknown> => {
    const patch: Record<string, unknown> = {};
    if (fields.year !== null && fields.year !== initialYear) patch.year = fields.year;
    const make = fields.make.trim();
    if (make !== '' && make !== initialMake) patch.make = make;
    const model = fields.model.trim();
    if (model !== '' && model !== initialModel) patch.model = model;
    if (fields.vin.trim() !== '') {
      const vin = normalizeVinCandidate(fields.vin);
      if (vin !== initialVin) patch.vin = vin;
    }
    return patch;
  }, [fields, initialYear, initialMake, initialModel, initialVin]);

  const patch = buildPatch();
  const hasChanges = Object.keys(patch).length > 0;

  const handleSave = useCallback(async () => {
    // Loud guard: never appear to save when there is no vehicle to save onto.
    if (typeof vehicleId !== 'number' || !Number.isFinite(vehicleId) || vehicleId <= 0) {
      message.error('This estimate has no vehicle attached — add a vehicle before capturing its details.');
      return;
    }
    if (fields.vin.trim() !== '' && !vinIsValid) {
      message.error('That VIN is not valid (17 characters, no I, O or Q). Fix it or clear it before saving.');
      return;
    }
    if (fields.year !== null && (fields.year < MIN_VEHICLE_YEAR || fields.year > maxVehicleYear())) {
      message.error(`Year must be between ${MIN_VEHICLE_YEAR} and ${maxVehicleYear()}.`);
      return;
    }
    if (!hasChanges) {
      message.info('Nothing to save yet — paste or type the vehicle details first.');
      return;
    }

    setSaving(true);
    try {
      await vehicleApi.update(vehicleId, patch as any);
      message.success('Vehicle details saved.');
      onSaved?.();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.error ?? err?.message ?? 'unknown error';
      logger.error('[VehicleQuickCapture] save failed', detail);
      message.error(`Could not save vehicle details: ${detail}`);
    } finally {
      setSaving(false);
    }
  }, [vehicleId, fields, vinIsValid, hasChanges, patch, onSaved, onClose]);

  const noVehicle = typeof vehicleId !== 'number' || vehicleId <= 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <Space size={6}>
          <ScanOutlined />
          <span>Capture vehicle details</span>
        </Space>
      }
      placement="bottom"
      height="90vh"
      destroyOnHidden
      data-testid="vqc-drawer"
      styles={{ body: { padding: 16, paddingBottom: 24, overflowY: 'auto' } }}
      footer={
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Button
            type="primary"
            block
            size="large"
            loading={saving}
            disabled={noVehicle}
            onClick={handleSave}
            data-testid="vqc-save"
          >
            Save to vehicle
          </Button>
          <Button block onClick={onClose}>
            Cancel
          </Button>
        </Space>
      }
    >
      {noVehicle && (
        <Alert
          type="error"
          showIcon
          data-testid="vqc-no-vehicle"
          style={{ marginBottom: 12 }}
          message="No vehicle attached"
          description="This record has no vehicle, so there is nowhere to save these details. Attach a vehicle first."
        />
      )}

      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Paste anything — a text from the customer, a listing, or just the VIN. Everything it
        recognises drops into the fields below; correct whatever is off.
      </Paragraph>

      <Input.TextArea
        value={pasted}
        onChange={(e) => handlePasteChange(e.target.value)}
        placeholder={'e.g. "2018 Honda Civic, vin 1hg bh4 1jx mn1 091 86"'}
        autoSize={{ minRows: 3, maxRows: 8 }}
        data-testid="vqc-paste"
        style={{ marginBottom: 12 }}
      />

      <Form layout="vertical" size="large">
        <Form.Item label="Year" style={{ marginBottom: 12 }}>
          <InputNumber
            value={fields.year ?? undefined}
            onChange={(v) => setField('year', typeof v === 'number' ? v : null)}
            min={MIN_VEHICLE_YEAR}
            max={maxVehicleYear()}
            // Digits only on a phone keypad; no thousands separator (a year is not money)
            inputMode="numeric"
            controls={false}
            style={{ width: '100%' }}
            data-testid="vqc-year"
          />
        </Form.Item>
        <Form.Item label="Make" style={{ marginBottom: 12 }}>
          <Input
            value={fields.make}
            onChange={(e) => setField('make', e.target.value)}
            maxLength={MAX_MAKE_MODEL_LENGTH}
            data-testid="vqc-make"
          />
        </Form.Item>
        <Form.Item label="Model" style={{ marginBottom: 12 }}>
          <Input
            value={fields.model}
            onChange={(e) => setField('model', e.target.value)}
            maxLength={MAX_MAKE_MODEL_LENGTH}
            data-testid="vqc-model"
          />
        </Form.Item>
        {/* Camera VIN scan. Reuses the existing CameraCaptureField (camera →
            attachment upload → POST /ocr/vin), which already handles the touch
            camera, the desktop webcam fallback and a low-confidence confirm step.
            Only rendered with a real vehicle to attach the photo to; OCR needs the
            shop's AI to be configured and surfaces a visible error when it is not. */}
        {typeof vehicleId === 'number' && vehicleId > 0 && (
          <div style={{ marginBottom: 12 }} data-testid="vqc-scan-wrap">
            <CameraCaptureField
              purpose="vin"
              entityType="vehicle"
              entityId={vehicleId}
              ocrEnabled
              buttonLabel="Scan VIN with camera"
              onUploaded={({ ocr }) => {
                if (!ocr?.value) return; // low confidence / no read — its own modal asks the user
                // Set it even if it does not validate: the field shows the error and
                // the user can fix a misread character rather than losing the scan.
                setField('vin', normalizeVinCandidate(ocr.value));
              }}
            />
          </div>
        )}

        <Form.Item
          label="VIN"
          style={{ marginBottom: 8 }}
          validateStatus={fields.vin.trim() !== '' && !vinIsValid ? 'error' : ''}
          help={
            fields.vin.trim() !== '' && !vinIsValid
              ? 'A VIN is 17 characters and never contains I, O or Q.'
              : undefined
          }
        >
          <Input
            value={fields.vin}
            onChange={(e) => setField('vin', e.target.value)}
            placeholder="17 characters"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            data-testid="vqc-vin"
          />
        </Form.Item>
        <Button
          block
          onClick={handleDecode}
          loading={decoding}
          disabled={!vinIsValid}
          data-testid="vqc-decode"
          style={{ marginBottom: 12 }}
        >
          Look up year / make / model from VIN
        </Button>
      </Form>

      {decodeNotes.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="VIN decode notes"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {decodeNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          }
        />
      )}

      {parsed && parsed.problems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          data-testid="vqc-problems"
          style={{ marginBottom: 12 }}
          message="Could not use these"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {parsed.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          }
        />
      )}

      {parsed && parsed.assumptions.length > 0 && (
        <Alert
          type="info"
          showIcon
          data-testid="vqc-assumptions"
          style={{ marginBottom: 12 }}
          message="Please confirm"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {parsed.assumptions.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          }
        />
      )}

      {parsed && parsed.unparsed.length > 0 && (
        <div data-testid="vqc-unparsed" style={{ marginBottom: 12 }}>
          <Text type="secondary" strong>
            Not recognised (left for you)
          </Text>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {parsed.unparsed.map((line) => (
              <li key={line}>
                <Text type="secondary">{line}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
};

export default VehicleQuickCapture;
